import { useEffect, useMemo, useRef } from "react"
import { toast } from "sonner"
import { ArrowLeft } from "@untitledui/icons"

import { ProtectedRoute } from "@/lib/auth/protected-route"
import { ALL_ROLES } from "@/lib/team/roles"
import { AppLayout } from "@/components/layout/app-layout"
import { useOrganization } from "@/lib/organization/organization-context"
import { useSearchParamState } from "@/lib/utils/use-search-param-state"
import { useTasks } from "@/lib/api/use-tasks"
import type { TaskRow } from "@/lib/api/tasks"
import { nextLine } from "@/lib/tasks/task-copy"
import { continuesInPlace, kindOf, matchesKindTab, type KindTab, type TaskResult } from "@/lib/tasks/task-types"
import { TaskList } from "@/components/tasks/task-list"
import { TaskWorkspace } from "@/components/tasks/task-workspace"
import { CardSkeleton, ListSkeleton, RailSkeleton } from "@/components/tasks/skeletons"
import { cx } from "@/lib/utils/cx"

// The screen where the advisor handles things. The stage tells her what to
// do; the system can't see a call happen, so it assigns a task and she
// answers it — and each answer is what generates the next one. Left: today's
// list, in the order to work it. Right: one customer, one question.

const WORK_ORDER = ["overdue", "now", "later"]

export default function TasksPage() {
  const { activeTeamId, activeTeamKind } = useOrganization()
  const teamId = activeTeamId != null ? Number(activeTeamId) : null
  const { data, isLoading } = useTasks(teamId)
  const [taskParam, setTaskParam] = useSearchParamState("task", "")
  const [estimateParam, setEstimateParam] = useSearchParamState("estimate", "")
  // Which kind she's batching — a tab, kept in the URL like the rest.
  const [kindParam, setKindParam] = useSearchParamState("kind", "")
  const tab: KindTab =
    kindParam === "call" || kindParam === "reply" || kindParam === "other" ? kindParam : "all"

  const rows = useMemo(() => data?.rows ?? [], [data?.rows])
  const shown = useMemo(() => rows.filter((r) => matchesKindTab(r, tab)), [rows, tab])
  // "Do all the calls" — advancing walks the tab, not the whole day.
  const workable = useMemo(() => shown.filter((r) => WORK_ORDER.includes(r.group)), [shown])
  const cadence = data?.cadence ?? null
  const selectedId = taskParam ? Number(taskParam) : null
  const selected = rows.find((r) => r.id === selectedId) ?? null

  // A task the engine just created for the SAME customer (wrong number → fix
  // it; last call → wrap up; review → first call) is selected before the list
  // has refetched it. Hold that selection until the row arrives.
  const awaitingRef = useRef<number | null>(null)
  if (awaitingRef.current != null && rows.some((r) => r.id === awaitingRef.current)) awaitingRef.current = null

  // Deep link from the board / dashboard: ?estimate=ID → that estimate's open
  // task. One URL write only — writing two params in the same tick makes the
  // second overwrite the first, which is how this used to land on the wrong
  // task. The estimate param just stays; it's inert once a task is chosen.
  useEffect(() => {
    if (!estimateParam || taskParam || rows.length === 0) return
    const hit = rows.find((r) => r.estimateId === Number(estimateParam) && r.group !== "done")
    setTaskParam(hit ? String(hit.id) : "")
    if (!hit) setEstimateParam("")
  }, [estimateParam, taskParam, rows, setTaskParam, setEstimateParam])

  // Land on the first thing owed; follow the list when the selection vanishes
  // (answered, or hidden by switching tabs).
  useEffect(() => {
    if (rows.length === 0) return
    if (selectedId != null && shown.some((r) => r.id === selectedId)) return
    if (selectedId != null && awaitingRef.current === selectedId) return
    if (estimateParam && !taskParam) return // the deep link picks first
    const first = workable[0] ?? shown[0]
    if (first && window.innerWidth >= 1024) setTaskParam(String(first.id))
  }, [rows, shown, workable, selectedId, estimateParam, taskParam, setTaskParam])

  const advance = (from: TaskRow) => {
    const idx = workable.findIndex((r) => r.id === from.id)
    const next = workable.slice(idx + 1).find((r) => r.id !== from.id) ?? workable.find((r) => r.id !== from.id)
    setTaskParam(next ? String(next.id) : "")
  }

  // The ONE place anything on this screen toasts: the line that says what
  // happens next, in the pipeline's words.
  const onDone = (row: TaskRow) => (result: TaskResult) => {
    toast.success(nextLine(result, row), { duration: 6000, className: "text-[14.5px] leading-snug" })
    const stay = continuesInPlace(row, result)
    if (stay) {
      awaitingRef.current = stay.id
      setTaskParam(String(stay.id))
      return
    }
    advance(row)
  }

  const switchToCall = (row: TaskRow) => {
    const phone = rows.find(
      (r) => r.estimateId === row.estimateId && kindOf(r.type) === "call" && r.group !== "done",
    )
    if (phone) setTaskParam(String(phone.id))
    else toast("No call is on your list for them right now — answer in the conversation.")
  }

  const toDo = workable.length
  const done = data?.counts.done ?? 0

  // The workspace column is the page scroll; a new task starts at its top.
  useEffect(() => {
    document.querySelector("main")?.scrollTo({ top: 0 })
  }, [selectedId])

  return (
    <ProtectedRoute allowedRoles={ALL_ROLES}>
      <AppLayout>
        <div className="min-h-full bg-zinc-50/40">
          <div className="mx-auto max-w-[1600px] px-4 md:px-6 py-4 md:py-5">
            {/* One compact line — the header must not cost list height. */}
            <header className="mb-4 flex items-baseline justify-between gap-4 flex-wrap">
              <div className="flex items-baseline gap-3">
                <h1 className="text-xl md:text-2xl font-bold text-zinc-900 tracking-tight">Tasks</h1>
                <p className="text-[14px] text-zinc-500 hidden sm:block">
                  {activeTeamKind === "bodyshop" ? "Today's calls, replies and visits, in order." : "Follow-up runs on the body shop side."}
                </p>
              </div>
              {data && (
                <p className="text-[14px] text-zinc-600 tabular-nums">
                  <span className="font-semibold text-zinc-900">{toDo}</span> to do
                  <span className="text-zinc-300 mx-2">·</span>
                  {/* Re-keyed on change so the count visibly ticks as the day empties. */}
                  <span key={done} className="inline-block font-semibold text-zinc-900 animate-in zoom-in-50 duration-300 motion-reduce:animate-none">
                    {done}
                  </span>{" "}
                  done today
                  <span className="hidden lg:inline text-zinc-500 ml-3 text-[12.5px]">↑↓ to move</span>
                </p>
              )}
            </header>

            {isLoading ? (
              <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[320px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(0,1fr)_340px] items-start">
                <div className="rounded-2xl border border-zinc-200/80 bg-white overflow-hidden hidden lg:block">
                  <ListSkeleton />
                </div>
                <CardSkeleton />
                <div className="hidden xl:block">
                  <RailSkeleton />
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div className="rounded-2xl border border-zinc-200/80 bg-white px-6 py-14 text-center">
                <p className="text-[18px] font-bold text-zinc-900 tracking-tight">Nothing owed</p>
                <p className="text-[14px] text-zinc-500 mt-2 max-w-md mx-auto">
                  Tasks appear when an estimate lands, when it's confirmed, when a customer replies, and a couple of days after each call.
                </p>
              </div>
            ) : (
              // Three columns from xl: the queue, the work, the context rail. At lg
              // the rail stacks under the work; below lg it's one column at a time.
              // minmax(0,1fr) everywhere: an implicit `auto` column let the slot
              // picker's day strip widen the whole page on a phone.
              <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[320px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(0,1fr)_340px] items-start">
                {/* The queue and the rail pin to the viewport and scroll on their
                    own; the work scrolls the page. */}
                <div className={cx("min-w-0 lg:sticky lg:top-0", selected && "hidden lg:block")}>
                  <TaskList
                    rows={rows}
                    cadence={cadence}
                    tab={tab}
                    onTab={(t) => setKindParam(t === "all" ? "" : t)}
                    selectedId={selectedId}
                    onSelect={(r) => setTaskParam(String(r.id))}
                    className="lg:h-[calc(100dvh-8.75rem)]"
                  />
                </div>
                {selected && teamId != null ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setTaskParam("")}
                      className="lg:hidden inline-flex items-center gap-1.5 h-10 px-2 -ml-2 rounded-lg text-[14px] font-medium text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100"
                    >
                      <ArrowLeft className="size-3.5" aria-hidden /> All tasks
                    </button>
                    <TaskWorkspace
                      key={selected.id}
                      row={selected}
                      teamId={teamId}
                      cadence={cadence}
                      onDone={onDone(selected)}
                      onSwitchToCall={selected.type === "reply" ? () => switchToCall(selected) : undefined}
                    />
                  </>
                ) : awaitingRef.current != null ? (
                  <>
                    <CardSkeleton />
                    <div className="hidden xl:block">
                      <RailSkeleton />
                    </div>
                  </>
                ) : (
                  <div className="hidden lg:block xl:col-span-2 rounded-2xl border border-dashed border-zinc-300 bg-white/60 p-10 text-center text-[14px] text-zinc-500">
                    Pick a task on the left.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </AppLayout>
    </ProtectedRoute>
  )
}
